<!-- app/javascript/dashboard/routes/dashboard/settings/emailTemplates/builder/panels/props/ProductProps.vue -->
<script setup>
// Produto: busca no cadastro (1 a 3), textos/preço editáveis, foto e link por
// item e "Atualizar do cadastro" (relê nome, descrição e preço).
import { computed, onBeforeUnmount, onMounted, ref, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MediaLibraryPanel from 'dashboard/routes/dashboard/marketing/pages/MediaLibraryPanel.vue';
import { useAlert } from 'dashboard/composables';
import ProductsAPI from 'dashboard/api/catalogProducts';
import ColorField from '../../fields/ColorField.vue';
import SpacingField from '../../fields/SpacingField.vue';
import AlignField from '../../fields/AlignField.vue';
import VisibilityField from '../../fields/VisibilityField.vue';
import { withScheme } from '../../document/links';
import {
  PRODUCT_MAX,
  PRODUCT_LAYOUTS,
  formatBRL,
  productToItem,
  refreshItems,
  applyRefresh,
} from '../../document/product';

const props = defineProps({
  node: { type: Object, required: true },
  selection: { type: Object, default: null },
});
const emit = defineEmits(['update']);
const ed = inject('emailEditor');
const { t } = useI18n();
const theme = computed(() => ed.template.value?.theme || {});
const st = computed(() => props.node.style || {});
const items = computed(() => props.node.items || []);
const style = (key, value) => emit('update', { style: { [key]: value } }, key);
const setItems = list => emit('update', { items: list }, 'items');
const setItem = (index, patch) =>
  setItems(
    items.value.map((item, i) => (i === index ? { ...item, ...patch } : item))
  );

// ---- busca no cadastro ----
const query = ref('');
const results = ref([]);
const searching = ref(false);
let timer = null;
let requestId = 0;
const search = () => {
  clearTimeout(timer);
  timer = setTimeout(async () => {
    requestId += 1;
    const mine = requestId;
    searching.value = true;
    try {
      const { data } = await ProductsAPI.get({
        q: query.value.trim(),
        active: true,
      });
      if (mine === requestId) results.value = (data || []).slice(0, 20);
    } catch (error) {
      if (mine === requestId) {
        useAlert(t('EMAIL_TEMPLATES.EDITOR.PRODUCT.SEARCH_ERROR'));
      }
    } finally {
      if (mine === requestId) searching.value = false;
    }
  }, 300);
};
onMounted(search);
onBeforeUnmount(() => clearTimeout(timer));

// Bloco novo vem com um item de exemplo: o primeiro produto toma o lugar dele.
const isPlaceholder = list =>
  list.length === 1 && !list[0].product_id && !list[0].media?.url;
const addProduct = product => {
  const base = isPlaceholder(items.value) ? [] : items.value;
  if (base.length >= PRODUCT_MAX) {
    useAlert(t('EMAIL_TEMPLATES.EDITOR.PRODUCT.MAX'));
    return;
  }
  setItems([
    ...base,
    productToItem(product, {
      button_label: t('EMAIL_TEMPLATES.EDITOR.PRODUCT.BUY'),
    }),
  ]);
};
const removeItem = index => setItems(items.value.filter((_, i) => i !== index));

const refreshing = ref(false);
const hasLinked = computed(() => items.value.some(i => i.product_id));
const refresh = async () => {
  if (refreshing.value || !hasLinked.value) return;
  refreshing.value = true;
  try {
    const out = await refreshItems(
      items.value,
      async id => (await ProductsAPI.show(id)).data
    );
    // Mescla na lista de agora: edições feitas durante a espera não se perdem.
    if (Object.keys(out.fresh).length) {
      setItems(applyRefresh(items.value, out.fresh));
    }
    if (out.missing.length) {
      useAlert(
        t('EMAIL_TEMPLATES.EDITOR.PRODUCT.MISSING', {
          names: out.missing.join(', '),
        })
      );
    } else if (out.failed.length) {
      useAlert(t('EMAIL_TEMPLATES.EDITOR.PRODUCT.REFRESH_ERROR'));
    } else {
      useAlert(t('EMAIL_TEMPLATES.EDITOR.PRODUCT.REFRESHED'));
    }
  } finally {
    refreshing.value = false;
  }
};

// ---- foto por item ----
const media = ref(null);
const photoIndex = ref(null);
const choosePhoto = index => {
  photoIndex.value = index;
  media.value?.open();
};
const pick = asset => {
  if (photoIndex.value !== null) {
    setItem(photoIndex.value, {
      media: { asset_id: asset.id, url: asset.url },
    });
  }
  media.value?.close();
};
const priceInput = value => (value === '' ? null : Number(value));
const inputClass =
  'w-full rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm';
</script>

<template>
  <div data-props="product" class="flex flex-col gap-4">
    <div class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.PRODUCT.LAYOUT')
      }}</span>
      <div
        class="flex gap-0.5 self-start rounded-lg border border-n-weak p-0.5"
      >
        <button
          v-for="l in PRODUCT_LAYOUTS"
          :key="l"
          type="button"
          class="rounded-md px-2 py-0.5 text-xs"
          :class="[
            node.layout === l
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11',
            items.length > 1 && l !== 'grid' ? 'opacity-40' : '',
          ]"
          :disabled="items.length > 1 && l !== 'grid'"
          @click="emit('update', { layout: l }, 'layout')"
        >
          {{ t(`EMAIL_TEMPLATES.EDITOR.PRODUCT.LAYOUTS.${l}`) }}
        </button>
      </div>
      <p v-if="items.length > 1" class="text-[11px] text-n-slate-10">
        {{ t('EMAIL_TEMPLATES.EDITOR.PRODUCT.LAYOUT_MULTI') }}
      </p>
    </div>
    <div class="flex flex-col gap-1.5">
      <input
        v-model="query"
        data-test="search"
        :class="inputClass"
        :placeholder="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.SEARCH')"
        @input="search"
      />
      <p v-if="searching" class="text-[11px] text-n-slate-10">
        {{ t('EMAIL_TEMPLATES.EDITOR.PRODUCT.SEARCHING') }}
      </p>
      <p v-else-if="!results.length" class="text-[11px] text-n-slate-10">
        {{ t('EMAIL_TEMPLATES.EDITOR.PRODUCT.NO_RESULTS') }}
      </p>
      <div class="flex max-h-40 flex-col gap-1 overflow-y-auto">
        <button
          v-for="p in results"
          :key="p.id"
          type="button"
          :data-result="p.id"
          class="flex items-center justify-between gap-2 rounded-lg border border-n-weak px-2 py-1.5 text-left text-xs hover:border-n-brand"
          @click="addProduct(p)"
        >
          <span class="min-w-0 truncate font-medium text-n-slate-12">{{
            p.name
          }}</span>
          <span class="shrink-0 text-n-slate-11">{{
            formatBRL(p.unit_price)
          }}</span>
        </button>
      </div>
    </div>
    <button
      type="button"
      data-test="refresh"
      class="self-start rounded-lg border border-n-weak px-3 py-1.5 text-xs text-n-slate-12 disabled:opacity-50"
      :disabled="refreshing || !hasLinked"
      @click="refresh"
    >
      {{ t('EMAIL_TEMPLATES.EDITOR.PRODUCT.REFRESH') }}
    </button>
    <div
      v-for="(item, i) in items"
      :key="i"
      :data-item="i"
      class="flex flex-col gap-2 rounded-lg border border-n-weak p-2"
    >
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold text-n-slate-11">{{
          t('EMAIL_TEMPLATES.EDITOR.PRODUCT.ITEM', { n: i + 1 })
        }}</span>
        <button
          v-if="items.length > 1"
          type="button"
          class="i-lucide-x size-4 text-n-slate-10 hover:text-n-ruby-11"
          :title="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.REMOVE')"
          @click="removeItem(i)"
        />
      </div>
      <input
        :class="inputClass"
        maxlength="120"
        :value="item.name"
        :placeholder="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.NAME')"
        @input="setItem(i, { name: $event.target.value })"
      />
      <textarea
        :class="inputClass"
        rows="2"
        maxlength="300"
        :value="item.description"
        :placeholder="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.DESCRIPTION')"
        @input="setItem(i, { description: $event.target.value })"
      />
      <div class="flex gap-2">
        <input
          :class="inputClass"
          type="number"
          min="0"
          step="0.01"
          :value="item.price ?? ''"
          :placeholder="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.PRICE')"
          @input="setItem(i, { price: priceInput($event.target.value) })"
        />
        <input
          :class="inputClass"
          type="number"
          min="0"
          step="0.01"
          :value="item.old_price ?? ''"
          :placeholder="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.OLD_PRICE')"
          @input="setItem(i, { old_price: priceInput($event.target.value) })"
        />
      </div>
      <input
        :class="inputClass"
        :value="item.url"
        :placeholder="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.URL')"
        @input="setItem(i, { url: $event.target.value })"
        @blur="setItem(i, { url: withScheme($event.target.value) })"
      />
      <input
        :class="inputClass"
        maxlength="40"
        :value="item.button_label"
        :placeholder="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.BUTTON_LABEL')"
        @input="setItem(i, { button_label: $event.target.value })"
      />
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="rounded-lg bg-n-brand px-3 py-1 text-xs font-medium text-white"
          @click="choosePhoto(i)"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.PRODUCT.PHOTO') }}
        </button>
        <button
          v-if="item.media?.url"
          type="button"
          class="text-xs text-n-ruby-11"
          @click="setItem(i, { media: { asset_id: null, url: null } })"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.PROPS.REMOVE_IMAGE') }}
        </button>
      </div>
    </div>
    <ColorField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.BUTTON_BG')"
      :theme="theme"
      :model-value="st.button_bg"
      @update:model-value="v => style('button_bg', v)"
    />
    <ColorField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.BUTTON_TEXT')"
      :theme="theme"
      allow-auto
      :model-value="st.button_color"
      @update:model-value="v => style('button_color', v)"
    />
    <AlignField
      :model-value="st.align"
      @update:model-value="v => style('align', v)"
    />
    <label class="flex items-center gap-2 text-xs text-n-slate-12">
      <input
        type="checkbox"
        :checked="st.card_border"
        @change="style('card_border', $event.target.checked)"
      />
      {{ t('EMAIL_TEMPLATES.EDITOR.PRODUCT.CARD_BORDER') }}
    </label>
    <SpacingField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.PADDING')"
      :model-value="st.padding"
      @update:model-value="v => style('padding', v)"
    />
    <VisibilityField
      v-if="selection?.row?.role !== 'footer'"
      :model-value="st.hide_on"
      @update:model-value="v => style('hide_on', v)"
    />
    <Dialog
      ref="media"
      :title="t('EMAIL_TEMPLATES.EDITOR.PRODUCT.PHOTO')"
      :show-confirm-button="false"
      width="3xl"
      overflow-y-auto
    >
      <MediaLibraryPanel pickable @pick="pick" />
    </Dialog>
  </div>
</template>
